import type {
  ScaleBarDistanceUnit,
  ScaleBarStyleId,
  ScaleBarUnits,
} from './layoutTypes';

/**
 * `line` styles draw a rule with ticks; `bar` styles draw filled/hollow
 * division boxes. The exporter uses the family to rasterize a close match.
 */
export type ScaleBarFamily = 'line' | 'bar';

export interface ScaleBarStyleMeta {
  readonly id: ScaleBarStyleId;
  readonly label: string;
  readonly family: ScaleBarFamily;
  readonly divisions: number;
  readonly dual: boolean;
  readonly defaultUnits?: readonly [ScaleBarDistanceUnit, ScaleBarDistanceUnit];
  readonly alignment?: 'left' | 'center' | 'right';
}

export const SCALE_BAR_STYLES: readonly ScaleBarStyleMeta[] = [
  { id: 'scaleLine1', label: 'Scale Line 1', family: 'line', divisions: 4, dual: false },
  { id: 'scaleLine2', label: 'Scale Line 2', family: 'line', divisions: 4, dual: false },
  { id: 'scaleLine3', label: 'Scale Line 3', family: 'line', divisions: 4, dual: false },
  { id: 'stepped', label: 'Stepped Scale Line', family: 'line', divisions: 4, dual: false },
  { id: 'alternating1', label: 'Alternating Scale Bar 1', family: 'bar', divisions: 4, dual: false },
  { id: 'alternating2', label: 'Alternating Scale Bar 2', family: 'bar', divisions: 4, dual: false },
  {
    id: 'doubleAlternating1',
    label: 'Double Alternating Scale Bar 1',
    family: 'bar',
    divisions: 4,
    dual: false,
  },
  {
    id: 'doubleAlternating2',
    label: 'Double Alternating Scale Bar 2',
    family: 'bar',
    divisions: 4,
    dual: false,
  },
  { id: 'hollow1', label: 'Hollow Scale Bar 1', family: 'bar', divisions: 4, dual: false },
  { id: 'hollow2', label: 'Hollow Scale Bar 2', family: 'bar', divisions: 4, dual: false },
  {
    id: 'singleDivision',
    label: 'Single Division Scale Bar',
    family: 'bar',
    divisions: 1,
    dual: false,
  },
];

export const DUAL_SCALE_BAR_STYLES: readonly ScaleBarStyleMeta[] = [
  {
    id: 'dualFeetMetersLineLeft',
    label: 'Feet and Meters Line Left',
    family: 'line',
    divisions: 4,
    dual: true,
    defaultUnits: ['feet', 'meters'],
    alignment: 'left',
  },
  {
    id: 'dualMilesKilometersLine',
    label: 'Miles and Kilometers Line',
    family: 'line',
    divisions: 4,
    dual: true,
    defaultUnits: ['miles', 'kilometers'],
    alignment: 'center',
  },
  {
    id: 'dualMilesKilometersLineLeft',
    label: 'Miles and Kilometers Line Left',
    family: 'line',
    divisions: 4,
    dual: true,
    defaultUnits: ['miles', 'kilometers'],
    alignment: 'left',
  },
  {
    id: 'dualMilesKilometersBar',
    label: 'Miles and Kilometers Bar',
    family: 'bar',
    divisions: 4,
    dual: true,
    defaultUnits: ['miles', 'kilometers'],
    alignment: 'center',
  },
  {
    id: 'dualMilesKilometersBarLeft',
    label: 'Miles and Kilometers Bar Left',
    family: 'bar',
    divisions: 4,
    dual: true,
    defaultUnits: ['miles', 'kilometers'],
    alignment: 'left',
  },
  {
    id: 'dualFeetMetersBarLeft',
    label: 'Feet and Meters Bar Left',
    family: 'bar',
    divisions: 4,
    dual: true,
    defaultUnits: ['feet', 'meters'],
    alignment: 'left',
  },
  {
    id: 'dualFeetMetersBarRight',
    label: 'Feet and Meters Bar Right',
    family: 'bar',
    divisions: 4,
    dual: true,
    defaultUnits: ['feet', 'meters'],
    alignment: 'right',
  },
  {
    id: 'dualFeetMetersLineRight',
    label: 'Feet and Meters Line Right',
    family: 'line',
    divisions: 4,
    dual: true,
    defaultUnits: ['feet', 'meters'],
    alignment: 'right',
  },
];

export const ALL_SCALE_BAR_STYLES: readonly ScaleBarStyleMeta[] = [
  ...SCALE_BAR_STYLES,
  ...DUAL_SCALE_BAR_STYLES,
];

export const SCALE_BAR_UNIT_GROUPS: readonly {
  readonly units: Exclude<ScaleBarUnits, 'dual'>;
  readonly label: string;
}[] = [
  { units: 'metric', label: 'Metric' },
  { units: 'imperial', label: 'Imperial' },
];

export function scaleBarStyleMeta(id: ScaleBarStyleId): ScaleBarStyleMeta {
  return ALL_SCALE_BAR_STYLES.find((style) => style.id === id) ?? SCALE_BAR_STYLES[0];
}

export function scaleBarStyleLabel(id: ScaleBarStyleId, units: ScaleBarUnits): string {
  const meta = scaleBarStyleMeta(id);
  if (meta.dual || units === 'dual') return meta.label;
  return `${meta.label} ${units === 'imperial' ? 'Imperial' : 'Metric'}`;
}
